import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cog, ChevronDown, Fuel, Heart, Luggage, ShieldCheck, Snowflake, Users, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MODEL_META, IMAGES } from "@/constants/site";

const Spec = ({ icon: Icon, label }) => (
  <span className="flex items-center gap-1.5 text-[11px] text-gray-600">
    <Icon size={13} className="text-gray-400 shrink-0" /> {label}
  </span>
);

const PriceRow = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-[5px] border-b border-gray-100 last:border-0">
    <span className="text-[11px] text-gray-500">{label}</span>
    <span className={`text-[11px] text-right ${strong ? "font-bold text-fr-navy" : "font-semibold text-gray-700"}`}>{value}</span>
  </div>
);

const CONDITIONS = [
  "Consegna gratuita da 5 a 90 minuti in base alla distanza: aeroporto, stazioni, hotel, B&B e casa.",
  "Km illimitati inclusi nel noleggio.",
  "Assicurazione RCA inclusa dove indicato; franchigie confermate dal gestionale.",
  "Età minima 21 anni, patente di categoria B.",
  "Nessuna carta di credito richiesta alla prenotazione: prezzo confermato dal gestionale.",
];

export const ModelCard = ({ group, category, days, onChoose, onDemo }) => {
  const [liked, setLiked] = useState(false);
  const [open, setOpen] = useState(false);
  const meta = MODEL_META[group.model] || {};
  const img = meta.img ? IMAGES.cars[meta.img] : null;
  const slug = `${group.brand}-${group.model}`.replace(/[^a-z0-9]/gi, "").toLowerCase();
  const daily = category?.daily_price;

  return (
    <div className="h-full flex flex-col rounded-2xl border border-gray-200 p-3.5 bg-white hover:shadow-lift hover:-translate-y-1 transition-all duration-300" data-testid={`model-card-${slug}`}>
      <div className="flex items-center justify-between">
        <span className="rounded-md bg-fr-ink text-white text-[10px] font-extrabold px-2 py-1 tracking-wide" data-testid="card-category-chip">
          {group.category.category}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400 font-medium" data-testid="card-availability">
            {group.qty} veicoli disponibili
          </span>
          <button onClick={() => setLiked(!liked)} aria-label="Preferiti" data-testid={`card-like-${slug}`}>
            <Heart size={16} className={liked ? "fill-fr-red text-fr-red" : "text-gray-300"} />
          </button>
        </div>
      </div>

      <button onClick={() => onDemo(group)} className="relative mt-2.5 aspect-[16/9] rounded-xl bg-gray-50 overflow-hidden flex items-center justify-center border border-gray-100 group/photo" data-testid={`card-photo-button-${slug}`}>
        {img ? (
          <img
            src={img}
            alt={`${group.brand} ${group.model} noleggio Napoli`}
            loading="lazy"
            className="w-full h-full object-contain p-2 group-hover/photo:scale-[1.06] transition-transform duration-500"
            data-testid="card-photo"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-[#16161a] to-black flex flex-col items-center justify-center text-center p-4">
            <span className="text-white font-extrabold text-lg">{group.brand}</span>
            <span className="text-gray-400 text-xs">{group.model}</span>
            <span className="text-[9px] text-gray-500 italic mt-2">Foto da gestionale</span>
          </div>
        )}
        <span className="absolute top-2 left-2 flex items-center gap-1 rounded-md bg-white/90 text-fr-navy text-[9px] font-extrabold px-2 py-1 border border-gray-200">
          <ZoomIn size={10} /> Demo
        </span>
        {category?.tag && (
          <span className="absolute bottom-2 left-2 rounded-md bg-fr-red text-white text-[9px] font-extrabold px-2 py-0.5 tracking-wide" data-testid="card-tag">
            {category.tag}
          </span>
        )}
      </button>

      <div className="mt-3">
        <h3 className="font-bold text-fr-ink leading-tight" data-testid="card-title">
          {group.brand} {group.model} <span className="text-gray-400 text-xs font-normal">o similare</span>
        </h3>
        <p className="mt-1 text-[11px] text-gray-500 leading-snug" data-testid="card-desc">
          {meta.desc}
        </p>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-x-2 gap-y-1.5">
        <Spec icon={Users} label={`${category.seats} posti`} />
        <Spec icon={Cog} label={(group.cambio && group.cambio.length === 1 ? group.cambio[0] : category.transmission)} />
        <Spec icon={Fuel} label={(group.alimentazione && group.alimentazione.length === 1 ? group.alimentazione[0] : category.fuel)} />
        <Spec icon={Snowflake} label="A/C" />
        <Spec icon={Luggage} label={`${category.bags} bagagli`} />
        <Spec icon={ShieldCheck} label={category.rca ? "RCA" : "—"} />
      </div>

      <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50/70 p-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-extrabold tracking-wider text-gray-400">PREZZO GIORNALIERO</span>
          <span className={`rounded-md text-[9px] font-extrabold px-2 py-0.5 tracking-wide ${daily != null ? "bg-green-100 text-green-800" : "bg-fr-sun text-fr-ink"}`} data-testid="card-price-badge">
            {daily != null ? "CONFERMATO GESTIONALE" : "DA GESTIONALE"}
          </span>
        </div>
        {daily != null ? (
          <p className="mt-1 text-lg font-extrabold text-fr-navy leading-none" data-testid="card-price-daily">
            da € {daily}
            <span className="text-[10px] font-semibold text-gray-400 ml-1">/ giorno</span>
          </p>
        ) : (
          <p className="mt-1 text-xs font-bold text-fr-navy" data-testid="card-price-note">
            Prezzo confermato dal gestionale
          </p>
        )}
        <div className="mt-2">
          <PriceRow label="Km illimitati inclusi" value="Incluso" strong />
          <PriceRow label="Deposito cauzionale" value="Da gestionale" />
          <PriceRow label="Franchigia danni e furto" value="Da gestionale" />
          <PriceRow label={`Totale ${days} giorni`} value={daily != null ? `€ ${Math.round(daily * days * 100) / 100}` : "Da calcolare"} strong />
        </div>
      </div>

      <div className="mt-auto pt-3.5">
        <button
          onClick={() => onChoose(group.category.category, `${group.brand} ${group.model}`)}
          className="w-full rounded-xl bg-fr-ink hover:bg-black active:scale-[0.98] text-white font-bold text-sm py-3 transition-all"
          data-testid={`card-choose-${slug}`}
        >
          Scegli modello • {group.brand} {group.model}
        </button>
        <button
          onClick={() => setOpen(!open)}
          className="mx-auto mt-2.5 flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-fr-navy transition-colors"
          data-testid={`card-details-toggle-${slug}`}
        >
          Dettagli e condizioni
          <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden text-[10px] text-gray-500 space-y-1.5 mt-2"
              data-testid="card-conditions"
            >
              {CONDITIONS.map((c) => (
                <li key={c} className="flex gap-1.5">
                  <span className="text-fr-red font-bold">•</span> {c}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const CarDemoModal = ({ group, category, days, onChoose, onClose }) => {
  const meta = group ? MODEL_META[group.model] || {} : {};
  const img = meta.img ? IMAGES.cars[meta.img] : null;
  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-24px)] sm:max-w-lg rounded-2xl p-0 overflow-hidden" data-testid="car-demo-dialog">
        {group && (
          <>
            <div className="relative aspect-[16/9] bg-gray-100 flex items-center justify-center">
              {img ? (
                <img src={img} alt={`${group.brand} ${group.model}`} className="w-full h-full object-contain p-3" />
              ) : (
                <div className="w-full h-full bg-gradient-to-b from-[#16161a] to-black flex flex-col items-center justify-center">
                  <span className="text-white font-extrabold text-xl">{group.brand}</span>
                  <span className="text-gray-400 text-sm">{group.model}</span>
                </div>
              )}
              <span className="absolute top-3 left-3 rounded-md bg-fr-ink text-white text-[10px] font-extrabold px-2 py-1">{group.category.category}</span>
            </div>
            <div className="p-5">
              <DialogTitle className="text-lg font-extrabold text-fr-ink" data-testid="car-demo-title">
                {group.brand} {group.model} <span className="text-gray-400 text-xs font-normal">o similare</span>
              </DialogTitle>
              <p className="mt-1.5 text-sm text-gray-600" data-testid="car-demo-desc">
                {meta.desc}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600">
                <span>Anni {group.years}</span>
                <span>{group.qty} veicoli in flotta</span>
                <span>{category.seats} posti</span>
                <span>{category.transmission}</span>
                <span>{category.fuel}</span>
                <span>A/C</span>
                <span>{category.bags} bagagli</span>
                {category.rca && <span>RCA inclusa</span>}
              </div>
              <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-3 text-[11px] text-gray-600">
                Prezzo giornaliero: <b className="text-fr-navy">{category.daily_price != null ? `€ ${category.daily_price}` : "DA GESTIONALE"}</b> — totale {days} giorni:{" "}
                <b className="text-fr-navy">{category.daily_price != null ? `€ ${Math.round(category.daily_price * days * 100) / 100}` : "Da calcolare"}</b>. Nessuna carta di credito richiesta.
              </div>
              <button
                onClick={() => {
                  onClose();
                  onChoose(group.category.category, `${group.brand} ${group.model}`);
                }}
                className="mt-4 w-full rounded-xl bg-fr-ink text-white font-bold text-sm py-3 hover:bg-black transition-colors"
                data-testid="car-demo-choose"
              >
                Prenota • {group.brand} {group.model}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModelCard;
